import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

export function validPublishedAppearance(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const contentRows = rows => Array.isArray(rows) && rows.length <= 200 && rows.every(row => row &&
    typeof row.id === 'string' && typeof row.slug === 'string' && typeof row.name === 'string' &&
    (row.content === undefined || typeof row.content === 'string'));
  if (!contentRows(value.pages) || !contentRows(value.blog)) return false;
  if (!value.menu || typeof value.menu !== 'object' || Array.isArray(value.menu)) return false;
  return ['public', 'signed'].filter(key => value.menu[key] !== undefined).length > 0 &&
    ['public', 'signed'].every(key => value.menu[key] === undefined || (Array.isArray(value.menu[key]) &&
      value.menu[key].length <= 100 && value.menu[key].every(row => row && typeof row.id === 'string' &&
        typeof row.name === 'string' && typeof row.page_link === 'string')));
}

export function appearanceLink(item, pages = []) {
  const slug = String(item.page_link || '').replace(/^\/+|\/+$/g, '');
  if (slug === 'external') {
    const value = item.url;
    if (typeof value !== 'string' || value.length > 2048 || /[\\\u0000-\u0020\u007f]/.test(value)) return null;
    try { const parsed = new URL(value); return ['http:', 'https:', 'mailto:'].includes(parsed.protocol) && !parsed.username && !parsed.password ? value : null; } catch { return null; }
  }
  if (['api', 'refill'].includes(slug) && pages.some(page => page.slug === slug)) return `/pages/${slug}`;
  const routes = { 'sign-in': '/login', 'sign-up': '/register', 'new-order': '/order',
    'mass-order': '/mass-order', services: '/services', orders: '/orders', payments: '/payments',
    'add-funds': '/payments', 'drip-feed': '/drip-feed', tickets: '/tickets', api: '/api-docs',
    affiliates: '/affiliates', 'child-panel': '/child-panel', settings: '/settings', blog: '/blog',
    refill: '/orders', updates: '/blog', account: '/settings', dashboard: '/dashboard' };
  if (routes[slug]) return routes[slug];
  if (pages.some(page => page.slug === slug)) return `/pages/${encodeURIComponent(slug)}`;
  return null;
}

const AppearanceContext = createContext(null);

// This provider lives above customer routes. Its current response survives a
// page unmount, but never a changed panel, account, API instance or retry.
export function AppearanceProvider({ enabled = true, customer = true, children }) {
  const appearance = useAppearanceRequest(enabled);
  return <AppearanceContext.Provider value={{ ...appearance, customer }}>{children}</AppearanceContext.Provider>;
}

// Lazy route fallbacks can read the current theme without starting a request
// on platform, owner or administrator surfaces.
export const useAppearanceSnapshot = () => useContext(AppearanceContext);

function useAppearanceRequest(enabled = true) {
  const { user, api } = useAuth();
  const { subdomain, tenantId, tenant } = useTenant();
  const [state, setState] = useState(null);
  const [version, setVersion] = useState(0);
  const signed = Boolean(user);
  const resolvedTenant = tenantId || tenant?.id || tenant?.tenant_id || '';
  const accountMatchesTenant = !signed || !user?.tenant_id || !resolvedTenant || user.tenant_id === resolvedTenant;
  const identity = `${window.location.hostname}:${user?.tenant_id || ''}:${user?.id || ''}:${user?.role || ''}:${subdomain || ''}:${resolvedTenant}`;
  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    const current = { identity, api, version, controller, data: null, error: '' };
    setState(current);
    if (!accountMatchesTenant) {
      setState({ ...current, error: 'Panel content could not be loaded.' });
      return () => controller.abort();
    }
    const request = signed ? api.get('/public/appearance/customer/current', { signal: controller.signal })
      : fetch(`/api/public/appearance/${encodeURIComponent(subdomain || window.location.hostname)}`, { signal: controller.signal })
        .then(async response => { if (!response.ok) throw new Error('Unavailable'); return { data: await response.json() }; });
    request.then(response => {
      if (!validPublishedAppearance(response.data)) throw new Error('Invalid appearance');
      if (!controller.signal.aborted) setState({ ...current, data: response.data });
    }).catch(() => { if (!controller.signal.aborted) setState({ ...current, error: 'Panel content could not be loaded.' }); });
    return () => controller.abort();
  // Depend on identity rather than object refreshes (balance updates, for example).
  }, [enabled, api, identity, subdomain, signed, version, accountMatchesTenant]);
  const current = enabled && accountMatchesTenant && state?.identity === identity && state?.api === api
    && state?.version === version && !state.controller.signal.aborted;
  const data = current ? state.data : null;
  const error = enabled && !accountMatchesTenant ? 'Panel content could not be loaded.' : current ? state.error : '';
  return { data, error, retry: () => setVersion(value => value + 1),
    loading: enabled && !data && !error, enabled, signed };
}

export default function useAppearance() {
  const shared = useAppearanceSnapshot();
  // Keep isolated callers supported, while disabling their request when the
  // persistent provider already owns it. Both hooks run on every render.
  const local = useAppearanceRequest(shared === null);
  return shared || local;
}
